/**
 * VRG-701 — the SUB-SOURCE of a lead source: the finer origin under a channel ("Couture Las
 * Vegas 2026" under Trade shows). It was a free-text field beside the channel; it is now picked
 * from a list that depends on the channel, and stored as a TYPED REFERENCE (kind + id) plus a
 * label snapshot, so deals can be grouped by it later and a rename does not orphan them.
 *
 *   channel                        list                       kind                where the list comes from
 *   #16 Trade shows                the trade-show calendar     `trade_show`        TRADE_SHOW_SUB_SOURCES (generated from GTM → Trade shows' snapshot)
 *   #13 Sales (outreach)           outreach campaigns          `outreach_campaign` `outreach_campaigns` (the table)
 *   #8 Content, #7 SEO             website articles            `article`           ARTICLE_SUB_SOURCES (generated from GTM → Articles' manifest snapshot)
 *   #14 Affiliate programs         referrers                   `referrer`          brands with a referral code (`referral_codes`) and Vera Insiders (`referrals.insider_ref`)
 *   #5 Social (organic)            social networks             `social_network`    SOCIAL_NETWORKS (gtm/social-network.ts): LinkedIn, Instagram, Other social. VRG-735: also
 *                                                                                  derived automatically from a lead's referrer host / utm_source
 *   every channel (also the above) a saved per-channel list    `saved`             `sales_sub_sources` (migration 0156); "Add sub-source" writes it
 *
 * The label is a SNAPSHOT taken when it was picked (the API resolves it from the list, never
 * from the client), so a sub-source whose campaign or article is later removed still reads.
 */
import {ARTICLE_SUB_SOURCES, TRADE_SHOW_SUB_SOURCES} from './sub-source-options.generated.js';
import {isSocialNetwork, SOCIAL_NETWORK_LABELS, SOCIAL_NETWORKS, type SocialNetwork} from './social-network.js';
import type {SourceChannel} from './source-channel.js';

// VRG-735 adds `social_network` (migration 0172 widens the two CHECKs).
export const SUB_SOURCE_KINDS = ['trade_show', 'outreach_campaign', 'article', 'referrer', 'social_network', 'saved'] as const;
export type SubSourceKind = (typeof SUB_SOURCE_KINDS)[number];
export const isSubSourceKind = (v: unknown): v is SubSourceKind => typeof v === 'string' && (SUB_SOURCE_KINDS as readonly string[]).includes(v);

export const SUB_SOURCE_KIND_LABELS: Readonly<Record<SubSourceKind, string>> = {
  trade_show: 'Trade shows',
  outreach_campaign: 'Outreach campaigns',
  article: 'Articles',
  referrer: 'Referrers',
  social_network: 'Social networks',
  saved: 'Saved for this channel',
};

/** The list a channel draws from, beside its saved list. Channels not here have only the saved list. */
export const CHANNEL_SUB_SOURCE_KIND: Readonly<Partial<Record<SourceChannel, Exclude<SubSourceKind, 'saved'>>>> = {
  trade_shows: 'trade_show',
  outreach: 'outreach_campaign',
  content_marketing: 'article',
  seo: 'article',
  affiliate_programs: 'referrer',
  social_display_ads: 'social_network',
};

/** A stored sub-source: what it points at, and the label it had when picked. */
export interface SubSourceRef {
  kind: SubSourceKind;
  id: string;
  label: string;
}

/** One option in a picker. `channel` is set for a saved option (it belongs to one channel). */
export interface SubSourceOption extends SubSourceRef {
  /** e.g. a trade show's city and month, an article's section. */
  hint?: string | null;
}

/** The grouping key: kind + id, so two spellings of one trade show are one sub-source. */
export const subSourceKey = (r: Pick<SubSourceRef, 'kind' | 'id'>): string => `${r.kind}:${r.id}`;

/** A sub-source from outside (a request body): {kind, id} with a known kind and a sane id, or null. */
export const parseSubSourceRef = (v: unknown): {kind: SubSourceKind; id: string} | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const {kind, id} = v as {kind?: unknown; id?: unknown};
  if (!isSubSourceKind(kind) || typeof id !== 'string' || !id.trim() || id.length > 200) return null;
  return {kind, id: id.trim()};
};

/** The generated lists, by kind, for the two kinds that have no table. */
export const tradeShowSubSource = (id: string): SubSourceOption | null => {
  const t = TRADE_SHOW_SUB_SOURCES.find(x => x.id === id);
  return t ? {kind: 'trade_show', id: t.id, label: t.label, hint: t.city} : null;
};
export const articleSubSource = (id: string): SubSourceOption | null => {
  const a = ARTICLE_SUB_SOURCES.find(x => x.id === id);
  return a ? {kind: 'article', id: a.id, label: a.label, hint: a.section === 'learn' ? 'Learn' : 'Insights'} : null;
};
export const TRADE_SHOW_SUB_SOURCE_OPTIONS: readonly SubSourceOption[] = TRADE_SHOW_SUB_SOURCES.map(t => ({kind: 'trade_show', id: t.id, label: t.label, hint: t.city}));
export const ARTICLE_SUB_SOURCE_OPTIONS: readonly SubSourceOption[] = ARTICLE_SUB_SOURCES.map(a => ({
  kind: 'article',
  id: a.id,
  label: a.label,
  hint: a.section === 'learn' ? 'Learn' : 'Insights',
}));

/** VRG-735 — the social networks under #5 Social, as sub-source options and references. */
export const SOCIAL_NETWORK_SUB_SOURCE_OPTIONS: readonly SubSourceOption[] = SOCIAL_NETWORKS.map(n => ({kind: 'social_network', id: n, label: SOCIAL_NETWORK_LABELS[n]}));
export const socialNetworkSubSource = (id: string): SubSourceRef | null =>
  isSocialNetwork(id) ? {kind: 'social_network', id, label: SOCIAL_NETWORK_LABELS[id]} : null;
/** The grouping key of a network's sub-source (`subSourceKey`), e.g. `social_network:linkedin`. */
export const socialNetworkSubSourceKey = (n: SocialNetwork): string => `social_network:${n}`;

export {ARTICLE_SUB_SOURCES, TRADE_SHOW_SUB_SOURCES};
